import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

const HISTORY_STATE_KEY = 'sceneaccessSearchScroll';
const listingSelector = 'a[data-search-listing-id]';
const locationURL = location => `${location.pathname}${location.search}${location.hash}`;

const readPosition = url => {
  const position = window.history.state?.[HISTORY_STATE_KEY];
  return position?.url === url &&
    typeof position.listingId === 'string' &&
    Number.isFinite(position.offset) &&
    Number.isFinite(position.scrollY) &&
    position.scrollY >= 0
    ? position
    : null;
};

// Listing cards use normal links, so React/Redux state does not survive the trip
// to a listing. The browser history entry does, including across a full reload.
const useSearchScrollRestoration = ({ listings, listingsAreLoaded }) => {
  const history = useHistory();
  const location = useLocation();
  const url = locationURL(location);
  const panelRef = useRef(null);
  const pendingPosition = useRef(null);
  const [pageShowCount, setPageShowCount] = useState(0);

  useEffect(() => {
    pendingPosition.current = history.action === 'POP' ? readPosition(url) : null;

    // A slow search must not pull the user back after they start interacting.
    const cancel = () => {
      pendingPosition.current = null;
      // Locale/profile hydration can remount the route. Once the user takes
      // over, a later remount must not reuse the position from the last visit.
      if (readPosition(url)) {
        try {
          const state = { ...window.history.state };
          delete state[HISTORY_STATE_KEY];
          window.history.replaceState(state, '');
        } catch (e) {
          // A restricted History API must not interrupt user input.
        }
      }
    };
    const onPageShow = event => {
      if (event.persisted) {
        // A back/forward-cache return does not remount this component.
        pendingPosition.current = readPosition(url);
        setPageShowCount(count => count + 1);
      }
    };
    const inputEvents = ['wheel', 'touchmove', 'pointerdown', 'keydown'];
    inputEvents.forEach(type => window.addEventListener(type, cancel, { passive: true }));
    window.addEventListener('pageshow', onPageShow);
    return () => {
      inputEvents.forEach(type => window.removeEventListener(type, cancel));
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [history, location.key, url]);

  useEffect(() => {
    if (!listingsAreLoaded || !pendingPosition.current) {
      return;
    }

    // Run after the route's initial scroll-to-top and the results' layout. This
    // also avoids restoring against the short loading page for signed-in users.
    const frame = window.requestAnimationFrame(() => {
      const position = pendingPosition.current;
      if (!position || !panelRef.current) {
        return;
      }
      const listing = Array.from(panelRef.current.querySelectorAll(listingSelector)).find(
        link => link.dataset.searchListingId === position.listingId
      );
      // Anchor to the listing if cards above it changed size or order. If it was
      // removed, retain the nearest available scroll position instead.
      const offset = Math.min(position.offset, Math.max(0, window.innerHeight - 100));
      const top = listing
        ? window.scrollY + listing.getBoundingClientRect().top - offset
        : position.scrollY;
      window.scrollTo({ top: Math.max(0, top), left: 0, behavior: 'instant' });
      pendingPosition.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [listings, listingsAreLoaded, location.key, url, pageShowCount]);

  const rememberListing = event => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    const link = event.target.closest?.(listingSelector);
    if (!link || !panelRef.current?.contains(link) || (link.target && link.target !== '_self')) {
      return;
    }
    try {
      // Preserve the router's key and state. Do not push/replace through the
      // router: that would rerun the search and reset scrolling before leaving.
      window.history.replaceState(
        {
          ...window.history.state,
          [HISTORY_STATE_KEY]: {
            url,
            listingId: link.dataset.searchListingId,
            offset: link.getBoundingClientRect().top,
            scrollY: window.scrollY,
          },
        },
        ''
      );
    } catch (e) {
      // History may be unavailable in restricted browsers; navigation must work.
    }
  };

  return { panelRef, rememberListing };
};

export default useSearchScrollRestoration;
